import { Box, Typography, styled } from '@mui/material';
import { X } from 'lucide-react';
import React from 'react';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { MOTION_TOKENS } from 'views/playground/features/data-lab/constants/motion-tokens';

type Props = {
  label: string;
  onClear: () => void;
};

const Root = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2),
  borderRadius: 8,
  border: `1px solid ${chartPalette.borders.strong}`,
  backgroundColor: chartPalette.backgrounds.raised,
}));

const Label = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: chartPalette.text.primary,
}));

const ClearButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  color: chartPalette.text.secondary,
  transition: `color ${MOTION_TOKENS.fade.duration}ms ${MOTION_TOKENS.fade.easing}`,

  '&:hover': {
    color: chartPalette.text.primary,
  },
}));

// The page supplies the same clear handler to this button, chart marks, and Escape.
const FilterChip: React.FC<Props> = ({ label, onClear }) => {
  const chartPalette = useChartPalette();

  return (
    <Root chartPalette={chartPalette}>
      <Label chartPalette={chartPalette}>{label}</Label>
      <ClearButton
        chartPalette={chartPalette}
        type="button"
        onClick={onClear}
        aria-label="Clear filter">
        <X size={14} />
      </ClearButton>
    </Root>
  );
};

export default FilterChip;
